import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { customerAPI } from '../../api/customers';
import { Customer } from '../../types/entities';
import { Plus, Edit, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

export const CustomerList: React.FC = () => {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      const data = await customerAPI.getAll();
      setCustomers(data);
    } catch (error) {
      toast.error(t('customers.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('customers.deleteConfirm'))) return;

    try {
      await customerAPI.delete(id);
      toast.success(t('customers.deleteSuccess'));
      loadCustomers();
    } catch (error) {
      toast.error(t('customers.deleteError'));
    }
  };

  if (loading) return <div className="text-center py-10">{t('common.loading')}</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">{t('customers.title')}</h1>
        <button
          onClick={() => navigate('/customers/new')}
          className="btn-primary flex items-center"
        >
          <Plus className="w-5 h-5 mr-2" />
          {t('customers.addNew')}
        </button>
      </div>

      <div className="card">
        <table className="w-full">
          <thead className="border-b">
            <tr className="text-left">
              <th className="pb-3 table-header">{t('customers.name')}</th>
              <th className="pb-3 table-header">{t('customers.phone')}</th>
              <th className="pb-3 table-header">{t('customers.address')}</th>
              <th className="pb-3 table-header">{t('common.status')}</th>
              <th className="pb-3 table-header">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {customers.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-8 text-center text-gray-500">
                  {t('common.noData')}
                </td>
              </tr>
            ) : (
              customers.map((customer) => (
                <tr key={customer.id} className="border-b last:border-0">
                  <td className="py-3 font-medium">{customer.name}</td>
                  <td className="py-3">{customer.phone || '-'}</td>
                  <td className="py-3">{customer.address || '-'}</td>
                  <td className="py-3">
                    <span
                      className={`px-2 py-1 rounded text-sm ${
                        customer.status === 'ACTIVE'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {customer.status === 'ACTIVE' ? t('common.active') : t('common.inactive')}
                    </span>
                  </td>
                  <td className="py-3">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => navigate(`/customers/${customer.id}/edit`)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        title={t('common.edit')}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(customer.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                        title={t('common.delete')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
